import { useForm, getFormProps, getInputProps } from "@conform-to/react"
import { getZodConstraint, parseWithZod } from "@conform-to/zod"
import { type ActionFunctionArgs } from "@remix-run/node"
import { useActionData, useSearchParams } from "@remix-run/react"
import { HoneypotInputs } from "remix-utils/honeypot/react"
import { z } from "zod"
import { GeneralErrorBoundary } from "#app/components/error-boundary.tsx"
import { Spacer } from "#app/components/spacer.tsx"
import {
	Form,
	FormContext,
	FormControl,
	FormErrorList,
	FormField,
	FormLabel,
	FormMessage,
} from "#app/components/ui/forms/conform-integrated/form"
import { Input } from "#app/components/ui/forms/conform-integrated/input"
import { StatusButton } from "#app/components/ui/status-button.tsx"
import { TypographyH1 } from "#app/components/ui/typography.tsx"
import { APP_NAME } from "#app/utils/constants.js"
import { checkHoneypot } from "#app/utils/honeypot.server.ts"
import { generateMeta } from "#app/utils/meta.js"
import { useIsPending } from "#app/utils/misc.tsx"
import { validateRequest } from "./verify.server"

export const codeQueryParam = "code"
export const targetQueryParam = "target"
export const typeQueryParam = "type"
export const redirectToQueryParam = "redirectTo"
const types = ["onboarding", "reset-password", "change-email", "2fa"] as const
const VerificationTypeSchema = z.enum(types)
export type VerificationTypes = z.infer<typeof VerificationTypeSchema>

export const VerifySchema = z.object({
	[codeQueryParam]: z.string().min(6).max(6),
	[typeQueryParam]: VerificationTypeSchema,
	[targetQueryParam]: z.string(),
	[redirectToQueryParam]: z.string().optional(),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	checkHoneypot(formData)
	return validateRequest(request, formData)
}

export const meta = generateMeta({
	title: `Verificación | ${APP_NAME}`,
	description: `Introduce el código de verificación que te hemos enviado por correo o en tu aplicación de autenticación.`,
})

export default function VerifyRoute() {
	const [searchParams] = useSearchParams()
	const isPending = useIsPending()
	const actionData = useActionData<typeof action>()
	const parseWithZoddType = VerificationTypeSchema.safeParse(
		searchParams.get(typeQueryParam),
	)
	const type = parseWithZoddType.success ? parseWithZoddType.data : null

	const checkEmail = (
		<>
			<TypographyH1>Comprueba tu correo</TypographyH1>
			<p className="mb-8 text-center text-base text-gray-700">
				Te hemos enviado un código de verificación.
			</p>
		</>
	)

	const headings: Record<VerificationTypes, React.ReactNode> = {
		onboarding: checkEmail,
		"reset-password": checkEmail,
		"change-email": checkEmail,
		"2fa": (
			<>
				<TypographyH1>Comprueba tu aplicación</TypographyH1>
				<p className="mb-8 text-center text-base text-gray-700">
					Por favor, introduce tu código 2FA para verificar tu identidad.
				</p>
			</>
		),
	}

	const [form, fields] = useForm({
		id: "verify-form",
		constraint: getZodConstraint(VerifySchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: VerifySchema })
		},
		defaultValue: {
			code: searchParams.get(codeQueryParam),
			type: type,
			target: searchParams.get(targetQueryParam),
			redirectTo: searchParams.get(redirectToQueryParam),
		},
	})

	return (
		<main className="container flex min-h-full flex-col justify-center">
			<div className="flex flex-col gap-4 text-center">
				{type ? headings[type] : "Tipo de verificación no válido"}
			</div>

			<Spacer size="3xs" />

			<FormContext context={form.context}>
				<div className="mx-auto flex w-72 max-w-full flex-col justify-center gap-1">
					<div>
						<FormErrorList formId={form.id} />
					</div>
					<div className="flex w-full gap-4">
						<Form
							method="POST"
							{...getFormProps(form)}
							className="flex flex-1 flex-col gap-4"
						>
							<HoneypotInputs />
							<FormField field={fields[codeQueryParam]}>
								<div className="flex flex-col gap-2">
									<FormLabel>Código</FormLabel>
									<FormControl>
										<Input
											meta={fields[codeQueryParam]}
											autoComplete="one-time-code"
										/>
									</FormControl>
									<FormMessage />
								</div>
							</FormField>
							<input
								{...getInputProps(fields[typeQueryParam], { type: "hidden" })}
							/>
							<input
								{...getInputProps(fields[targetQueryParam], { type: "hidden" })}
							/>
							<input
								{...getInputProps(fields[redirectToQueryParam], {
									type: "hidden",
								})}
							/>
							<StatusButton
								className="w-full"
								status={isPending ? "pending" : form.status ?? "idle"}
								type="submit"
								disabled={isPending}
							>
								Confirmar
							</StatusButton>
						</Form>
					</div>
				</div>
			</FormContext>
		</main>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
